/**
 * Google Maps rendering-type selection.
 *
 * Our map IDs are configured as VECTOR maps, which render through WebGL. Safari
 * loses that WebGL context and never recovers: the console fills with hundreds of
 * `WebGL: context lost.` lines and Google's own renderer throws
 * `LostContextError: getParameter` (webgl.js — it throws deliberately from
 * `if (a === null && this.isContextLost())`), which reaches React as an unhandled
 * rejection and takes over the page with the dev error overlay. Chrome is fine.
 *
 * A RASTER map has no WebGL context to lose, so it cannot fail this way. Nothing
 * in this app uses a vector-only feature — no tilt, heading, altitude or
 * WebGLOverlayView (BuyerZoneDrawMap explicitly forces tilt/heading to 0) — so
 * raster costs us nothing but the slightly different label/zoom rendering.
 *
 * Applied on Safari ONLY, deliberately: Chrome renders vector maps correctly
 * today, and there is no reason to change what already works for most users.
 *
 * ⚠️ If a map still renders as vector on Safari after this, the map ID's own
 * Cloud Console rendering type is winning over this option — the precedence
 * between the two is undocumented. The fix in that case is to flip the map ID to
 * Raster in Cloud Console (or point the env var at a raster map ID); no further
 * code change is needed.
 */

/**
 * Safari — and only Safari. Chrome, Edge, and Chrome-on-iOS all carry "Safari"
 * in their UA string, so each has to be excluded explicitly.
 */
export const isSafari = (userAgent) => {
    const ua = userAgent
        != null ? userAgent
        : (typeof navigator !== 'undefined' ? navigator.userAgent : '');
    if (!ua) return false;
    if (!/safari/i.test(ua)) return false;
    return !/chrome|chromium|crios|crmo|android|edg|edgios|opr|fxios/i.test(ua);
};

/**
 * The `renderingType` to merge into MapOptions, or null when the browser should
 * keep Google's default for the map ID.
 *
 * Deliberately does NOT require `window.google` to be present: callers build
 * their options object inside a `useMemo(..., [])` that can run a render before
 * the Maps script finishes loading, and a null here would be memoised forever —
 * silently leaving Safari on vector. `RenderingType` is a string enum, so the
 * literal is an exact substitute; we still prefer the real enum when it's loaded.
 */
export const RASTER = 'RASTER';

export const mapRenderingType = () => {
    if (!isSafari()) return null;
    const RenderingType = typeof window !== 'undefined'
        && window.google
        && window.google.maps
        && window.google.maps.RenderingType;
    return (RenderingType && RenderingType.RASTER) || RASTER;
};

/**
 * Spread-ready: `{ ...rasterOnSafari(), ...rest }`. Empty object on every browser
 * that renders vector maps correctly.
 */
export const rasterOnSafari = () => {
    const renderingType = mapRenderingType();
    return renderingType ? { renderingType } : {};
};
